import { Component } from 'react';

/**
 * Top-level error boundary mounted in inertia.jsx. Catches uncaught render
 * errors anywhere in the page tree and shows a recoverable panel instead of
 * blanking the screen — the previous behavior was that any throw inside (e.g.)
 * a UserMenu dropdown unmounted the whole app, leaving a white page with no
 * indication of what went wrong.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    if (typeof console !== 'undefined') {
      console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-white dark:bg-gray-900">
        <div className="max-w-lg w-full">
          <div className="rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-6">
            <h1 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">
              Something went wrong on this page.
            </h1>
            <p className="text-sm text-red-800 dark:text-red-300 mb-4">
              The error has been logged to the browser console. Try reloading
              or going back; if it keeps happening, share the console output
              with us.
            </p>
            <details className="mb-4">
              <summary className="text-xs font-mono text-red-700 dark:text-red-400 cursor-pointer">
                {String(this.state.error?.message || this.state.error || 'Unknown error')}
              </summary>
              {this.state.info?.componentStack && (
                <pre className="mt-2 text-[11px] text-red-700 dark:text-red-400 whitespace-pre-wrap overflow-x-auto">
                  {this.state.info.componentStack}
                </pre>
              )}
            </details>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Reload page
              </button>
              <button
                onClick={() => window.history.back()}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
